"use client"

import { useMemo } from "react"
import { FormikProvider, type FormikErrors, useFormik } from "formik"

import { EditDeliveryIntegrationFormFields, createDeliveryIntegrationInitialValues, normalizeDeliveryIntegrationFormConfig, type DeliveryIntegrationFormValues } from "@/components/deliveries/delivery-integration-form"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useUpdateDeliveryIntegrationMutation } from "@/hooks/mutations/delivery-integrations"
import type { DeliveryIntegrationView } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"


export function EditDeliveryIntegrationDialog({
  integration,
  onOpenChange,
}: {
  integration: DeliveryIntegrationView | null
  onOpenChange: (open: boolean) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesEditDeliveryIntegrationDialog")
  const tCommonUi = useTranslations("Common")

  const mutation = useUpdateDeliveryIntegrationMutation()
  const initialValues = useMemo(
    () => createDeliveryIntegrationInitialValues(integration ?? undefined),
    [integration],
  )
  const formik = useFormik<DeliveryIntegrationFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      if (!integration || !values.provider) return
      try {
        const normalizedConfig = normalizeDeliveryIntegrationFormConfig(
          values.provider,
          values.config,
        )
        const originalConfig = normalizeDeliveryIntegrationFormConfig(
          integration.provider,
          integration.config,
        )
        const configChanged = JSON.stringify(normalizedConfig)
          !== JSON.stringify(originalConfig)
        await mutation.mutateAsync({
          provider: values.provider,
          integrationId: integration.id,
          name: values.name,
          ...(configChanged ? { config: normalizedConfig } : {}),
          ...(values.apiKey.trim() ? { apiKey: values.apiKey } : {}),
          setErrors: errors => helpers.setErrors(
            errors as FormikErrors<DeliveryIntegrationFormValues>,
          ),
        })
        onOpenChange(false)
      } catch {
        // withQueryHandling has already surfaced the safe server error.
      }
    },
  })

  return (
    <Dialog open={integration !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overscroll-contain overflow-x-hidden overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle><span key={tUi("editDeliveryConnection")}>{tUi("editDeliveryConnection")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("senderOrAPIChangesAreTestedBeforeThe")}>{tUi("senderOrAPIChangesAreTestedBeforeThe")}</span></DialogDescription>
        </DialogHeader>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="min-w-0 space-y-6">
            {integration ? (
              <EditDeliveryIntegrationFormFields integrationId={integration.id} />
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={formik.isSubmitting}
              >
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button type="submit" disabled={formik.isSubmitting || !integration}>
                <span key={formik.isSubmitting ? tUi("saving") : tUi("saveTest")}>{formik.isSubmitting ? tUi("saving") : tUi("saveTest")}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}
